import { trackError } from '@proposify/frontend-observability';
import type { useAppDispatch } from '../grid/reduxStore/Store';
import { deleteSection, insertImportedSections } from '../grid/reduxStore/editorSlice';
import { deleteBlockSettings, loadImportedSectionBlockStyles } from '../grid/reduxStore/blockStyleSettingsSlice';
import { setSaveStatus } from '../grid/reduxStore/saveStatusSlice';
import { DocumentSaveStatus } from '../shared/models/DocumentSaveStatus';
import type { UndoRedoCommandInterface } from './undoRedoCommandVersion';
import { deleteSectionViaSocket } from '../grid/reduxStore/saveHandlers';
import type { SectionContentType } from '../Sections/Section.model.ts';
import { importPdfIntoDocument } from '../../../services/api/importPdfIntoDocument';

export interface ImportPdfIntoDocumentCommandDependencies {
  dispatch: ReturnType<typeof useAppDispatch>;
  socketClient: any;
}

export interface ImportPdfIntoDocumentCommandParams {
  contentId: string;
  presignedUrl: string;
  filename: string;
  afterSectionId?: string;
  pdfPath?: string;
}

export class ImportPdfIntoDocumentCommand implements UndoRedoCommandInterface {
  readonly scope = 'section' as const;
  readonly timestamp: string;

  private readonly dispatch: ReturnType<typeof useAppDispatch>;
  private readonly socketClient: any;

  private readonly contentId: string;
  private readonly presignedUrl: string;
  private readonly filename: string;
  private readonly afterSectionId: string | undefined;
  private readonly pdfPath: string | undefined;
  private importedSections: SectionContentType[];

  constructor(dependencies: ImportPdfIntoDocumentCommandDependencies, params: ImportPdfIntoDocumentCommandParams) {
    this.timestamp = new Date().toISOString();
    this.dispatch = dependencies.dispatch;
    this.socketClient = dependencies.socketClient;
    this.contentId = params.contentId;
    this.presignedUrl = params.presignedUrl;
    this.filename = params.filename;
    this.afterSectionId = params.afterSectionId;
    this.pdfPath = params.pdfPath;
    this.importedSections = [];
  }

  async execute(): Promise<string[] | null> {
    const traceparent = crypto.randomUUID();
    try {
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      const { sections } = await importPdfIntoDocument({
        contentId: this.contentId,
        presignedUrl: this.presignedUrl,
        filename: this.filename,
        afterSectionId: this.afterSectionId,
        pdfPath: this.pdfPath,
        traceparent,
      });

      this.importedSections = sections;
      this.dispatch(insertImportedSections({ sections, afterSectionId: this.afterSectionId }));
      this.dispatch(loadImportedSectionBlockStyles({ sections }));
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVED }));

      return sections.map((s) => s.id);
    } catch (error) {
      trackError(error instanceof Error ? error : new Error(String(error)), {
        errorFrom: 'editor-remote',
        feature: 'pdf-import',
        operation: 'execute',
        contentId: this.contentId,
        filename: this.filename,
        traceparent,
      });
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
      return null;
    }
  }

  async undo(): Promise<boolean> {
    if (this.importedSections.length === 0) {
      console.warn('ImportPdfIntoDocumentCommand: cannot undo, no imported sections recorded');
      return false;
    }

    try {
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      let allSucceeded = true;
      for (const section of [...this.importedSections].reverse()) {
        try {
          await deleteSectionViaSocket(this.socketClient, { sectionId: section.id });
          this.dispatch(deleteSection({ sectionId: section.id }));
          section.blocks.forEach((block) => {
            if (block.blockId) this.dispatch(deleteBlockSettings({ blockId: block.blockId }));
          });
        } catch (error) {
          trackError(error instanceof Error ? error : new Error(String(error)), {
            errorFrom: 'editor-remote',
            feature: 'pdf-import',
            operation: 'undo-delete-section',
            contentId: this.contentId,
            sectionId: section.id,
          });
          allSucceeded = false;
        }
      }

      this.dispatch(setSaveStatus({ status: allSucceeded ? DocumentSaveStatus.SAVED : DocumentSaveStatus.NOT_SAVED }));
      return allSucceeded;
    } catch (error) {
      trackError(error instanceof Error ? error : new Error(String(error)), {
        errorFrom: 'editor-remote',
        feature: 'pdf-import',
        operation: 'undo',
        contentId: this.contentId,
        filename: this.filename,
      });
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
      return false;
    }
  }

  async redo(): Promise<boolean> {
    const traceparent = crypto.randomUUID();
    try {
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      const { sections } = await importPdfIntoDocument({
        contentId: this.contentId,
        presignedUrl: this.presignedUrl,
        filename: this.filename,
        afterSectionId: this.afterSectionId,
        pdfPath: this.pdfPath,
        traceparent,
      });

      this.importedSections = sections;
      this.dispatch(insertImportedSections({ sections, afterSectionId: this.afterSectionId }));
      this.dispatch(loadImportedSectionBlockStyles({ sections }));
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVED }));

      return true;
    } catch (error) {
      trackError(error instanceof Error ? error : new Error(String(error)), {
        errorFrom: 'editor-remote',
        feature: 'pdf-import',
        operation: 'redo',
        contentId: this.contentId,
        filename: this.filename,
        traceparent,
      });
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
      return false;
    }
  }

  getImportedSectionIds(): string[] {
    return this.importedSections.map((s) => s.id);
  }

  toJSON(): Record<string, any> {
    return {
      contentId: this.contentId,
      filename: this.filename,
      afterSectionId: this.afterSectionId,
      importedSectionIds: this.importedSections.map((s) => s.id),
    };
  }
}
